import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Icon } from "./Icon";
import { useI18n } from "../lib/i18n";
import { recoveryIssuedAt } from "../lib/auth";

/**
 * "Choose a new password", for someone who came in through a reset link.
 *
 * The link signs them in — the main site verifies it — but signing in is only
 * half of what they asked for. Without this they would be in, with the
 * password they forgot still the only one on the account, and would need
 * another reset the next time. Shown once per reset link (keyed by when it was
 * issued), dismissable, and never to anyone who signed in any other way.
 */

const seenKey = (issuedAt: number) => `homeschool-recovery:${issuedAt}`;

function alreadyHandled(issuedAt: number) {
  try {
    return localStorage.getItem(seenKey(issuedAt)) !== null;
  } catch {
    return false;
  }
}

function markHandled(issuedAt: number) {
  try {
    localStorage.setItem(seenKey(issuedAt), "1");
  } catch {
    /* Storage is optional; the prompt simply may appear once more. */
  }
}

export function NewPasswordPrompt({
  supabase,
  onSaved,
}: {
  supabase: SupabaseClient;
  /** A confirmation for the app to show once the new password is in place. */
  onSaved: (message: string) => void;
}) {
  const { t } = useI18n();
  const [issuedAt, setIssuedAt] = useState<number | null>(null);
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState("");

  useEffect(() => {
    let live = true;
    void supabase.auth.getSession().then(({ data }) => {
      const token = data.session?.access_token;
      const at = token ? recoveryIssuedAt(token) : null;
      if (live && at && !alreadyHandled(at)) setIssuedAt(at);
    });
    return () => {
      live = false;
    };
  }, [supabase]);

  if (!issuedAt) return null;

  function dismiss() {
    if (issuedAt) markHandled(issuedAt);
    setIssuedAt(null);
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (saving) return;
    if (password.length < 8) {
      setProblem(t("auth.passwordHint"));
      return;
    }
    setSaving(true);
    setProblem("");
    const { error } = await supabase.auth.updateUser({ password });
    setSaving(false);
    if (error) {
      setProblem(/weak|should/i.test(error.message) ? t("auth.problem.weakPassword") : error.message);
      return;
    }
    dismiss();
    onSaved(t("auth.passwordSaved"));
  }

  return (
    <div className="modal-layer" role="dialog" aria-modal="true" aria-labelledby="new-password-title">
      <form className="modal-card glass new-password-card" onSubmit={save}>
        <button type="button" className="modal-close" onClick={dismiss} aria-label={t("common.close")}>
          <Icon name="close" />
        </button>
        <span className="icon-tile gold" aria-hidden="true">
          <Icon name="shield" />
        </span>
        <h2 id="new-password-title">{t("auth.newPasswordTitle")}</h2>
        <p>{t("auth.newPasswordCopy")}</p>
        <div className="auth-password-field">
          <input
            type={visible ? "text" : "password"}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            minLength={8}
            autoFocus
            aria-label={t("join.password")}
          />
          <button
            type="button"
            className="auth-eye"
            onClick={() => setVisible((value) => !value)}
            aria-label={visible ? t("auth.hidePassword") : t("auth.showPassword")}
            aria-pressed={visible}
          >
            <Icon name="eye" size={17} />
          </button>
        </div>
        {problem && (
          <p className="form-problem" role="alert">
            {problem}
          </p>
        )}
        <div className="modal-actions">
          <button type="button" className="soft-button" onClick={dismiss} disabled={saving}>
            {t("auth.notNow")}
          </button>
          <button type="submit" className="primary" disabled={saving}>
            <Icon name="check" /> {saving ? t("evidence.saving") : t("auth.savePassword")}
          </button>
        </div>
      </form>
    </div>
  );
}
